import { Fragment, type ComponentType, type ReactNode } from 'react';
import { useAuth } from './auth-context.js';

/**
 * 画面の差し込み口（スロット）。
 *
 * 業務モジュール同士は依存できない（03 第 I 部 §4 ルール 4）が、「顧客カードにその顧客のファイル・会議・ナレッジを出す」
 * 「稟議の起票中に似た過去の稟議を出す」のように、**画面は他モジュールの部品を並べたい**（03 §14.5・§18.1・02 §6）。
 * そこで: 置く側（ホスト）は `<Slot name="record.panel" subjectType="customer" … />` とだけ書き、
 * 出す側（プロバイダ）は自分の `ui/` から `SlotEntry[]` を export する。両者を結ぶのは組み立て（apps/web）だけ。
 *
 *  - ホストはプロバイダを知らない。プロバイダが 1 つも無ければ何も描かない
 *  - モジュールが無効なテナント・権限の無い人には出さない（ここで判定する。部品側でも API の 403 を空として扱うこと）
 *  - 部品は自分で読込 / 空 / エラーを持つ。**空のときは null を返して場所を取らない**のが既定
 */
export type SlotName =
  /** レコードの詳細画面の下 or 横に並ぶパネル（顧客・商談・案件・契約・稟議・会議 …） */
  | 'record.panel'
  /** レコードの見出しに置く操作（資料のサイドパネルなど）。 */
  | 'record.actions'
  /** 案件のタスク管理を外部モジュールに委ねる。 */
  | 'project.tasks'
  /** 入力フォームの横に出る助け（文脈サジェスト・03 §14.5）。context に入力中の題名・本文などが入る */
  | 'form.assist'
  /** 会議の要約を確定した直後に出すもの（会議後の 1 問・03 §15.2） */
  | 'meeting.afterSummary'
  /** ダッシュボード「自分宛て」に並ぶブロック（03 §12.10.3） */
  | 'dashboard.inbox'
  /**
   * 会話（チャット・レコード会話）の見出しに置く、相手への操作（「電話をかける」など・03 §11.4 #5）。
   * subjectType は DM の相手なら 'user'（subjectId = 相手のユーザー ID）、レコード会話なら会話の subject（customer / contact / lead / deal …）
   */
  | 'conversation.actions';

export interface SlotProps {
  /** customer / contact / deal / project / contract / workflow_request / expense_request / meeting / report … */
  subjectType: string;
  subjectId: string | null;
  /** 表示用の題名（レコード名）。分かるときだけ */
  title?: string | null;
  /** ホストが渡す追加の文脈（customerId・入力中の本文など）。キーはホストの README に書く */
  context?: Readonly<Record<string, unknown>>;
}

export interface SlotEntry {
  /** 一意な ID（'<module>.<name>'）。二重登録は後勝ちではなく無視 */
  id: string;
  /** 出す側のモジュール名。テナントで無効なら描かない */
  module: string;
  slot: SlotName;
  /** 対象の subjectType。省略 = 全部 */
  subjectTypes?: readonly string[];
  /** どれか 1 つ持っていれば出す。省略 = 権限を問わない */
  permission?: readonly string[];
  /** 小さいほど上。既定 100 */
  order?: number;
  Component: ComponentType<SlotProps>;
}

const registry: SlotEntry[] = [];

/** 組み立て（apps/web）が起動時に 1 回呼ぶ */
export function registerSlots(entries: readonly SlotEntry[]): void {
  for (const e of entries) {
    if (registry.some((r) => r.id === e.id)) continue;
    registry.push(e);
  }
  registry.sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.id.localeCompare(b.id));
}

/** テスト用 */
export function resetSlots(): void {
  registry.length = 0;
}

export function slotEntriesFor(name: SlotName, subjectType: string): readonly SlotEntry[] {
  return registry.filter((e) => e.slot === name && (!e.subjectTypes || e.subjectTypes.includes(subjectType)));
}

export interface SlotHostProps extends SlotProps {
  name: SlotName;
  /** 1 件ごとに包む（余白・見出しをホストの流儀に合わせたいとき） */
  wrap?: (node: ReactNode, entry: SlotEntry) => ReactNode;
}

export function Slot({ name, wrap, ...props }: SlotHostProps): ReactNode {
  const { isModuleEnabled, can } = useAuth();
  const entries = slotEntriesFor(name, props.subjectType).filter((e) => isModuleEnabled(e.module) && (!e.permission || e.permission.some((p) => can(p))));
  if (entries.length === 0) return null;
  return (
    <>
      {entries.map((e) => {
        const node = <e.Component {...props} />;
        return <Fragment key={e.id}>{wrap ? wrap(node, e) : node}</Fragment>;
      })}
    </>
  );
}
